import type { IStorageService } from "@/application/ports/services";
import { logger } from "@/infrastructure/logger";

export class SessionStorageService implements IStorageService {
	get<T>(key: string): T | null {
		try {
			const item = sessionStorage.getItem(key);
			if (item === null) {
				return null;
			}

			// Try to parse as JSON first
			try {
				return JSON.parse(item) as T;
			} catch {
				// If JSON parsing fails, return the raw string (for simple string values)
				logger.debug(
					`Item in sessionStorage (key: ${key}) is not JSON, returning as string`,
				);
				return item as T;
			}
		} catch (error) {
			logger.error(`Error reading from sessionStorage (key: ${key}):`, error);
			return null;
		}
	}

	set<T>(key: string, value: T): void {
		try {
			// If the value is a string, store it directly
			// If it's an object, stringify it
			if (typeof value === "string") {
				sessionStorage.setItem(key, value);
			} else {
				sessionStorage.setItem(key, JSON.stringify(value));
			}
		} catch (error) {
			logger.error(`Error writing to sessionStorage (key: ${key}):`, error);
			throw error;
		}
	}

	remove(key: string): void {
		try {
			sessionStorage.removeItem(key);
		} catch (error) {
			logger.error(`Error removing from sessionStorage (key: ${key}):`, error);
		}
	}

	clear(): void {
		try {
			sessionStorage.clear();
		} catch (error) {
			logger.error("Error clearing sessionStorage:", error);
		}
	}

	exists(key: string): boolean {
		try {
			return sessionStorage.getItem(key) !== null;
		} catch (error) {
			logger.error(
				`Error checking sessionStorage existence (key: ${key}):`,
				error,
			);
			return false;
		}
	}
}
